import { useEffect, useMemo, useRef, useState } from "react";
import { useDataSource } from "@firecms/core";
import { getApp } from "firebase/app";
import {
  collection,
  doc,
  getFirestore,
  onSnapshot,
  type DocumentData,
} from "firebase/firestore";

import { eventTaskPath, parseEventTask, type EventTask } from "../collections/eventTask";
import {
  buildEventsPath,
  eventsCollection,
  type Event,
} from "../collections/events";
import { buildLevelsCollection, buildLevelsPath } from "../collections/levels";
import { buildStoriesCollection, buildStoriesPath } from "../collections/stories";
import { buildUnitCollection, buildUnitsPath } from "../collections/units";
import {
  aggregateEventContentOverview,
  type EventContentCatalogLevel,
  type EventContentCatalogStory,
  type EventContentCatalogUnit,
  type EventContentOverview,
  type StoredLevelStats,
} from "../eventContent/aggregateEventContentOverview";
import { eventContentScopeId } from "../eventContent/eventContentScope";
import { useAdminTranslation } from "../i18n/useAdminTranslation";
import type { EventCollectionId } from "../workspace/collectionIds";

type Catalog = {
  units: EventContentCatalogUnit[];
  levels: EventContentCatalogLevel[];
  stories: EventContentCatalogStory[];
};

function asStarCounts(value: unknown): [number, number, number, number] {
  if (!Array.isArray(value) || value.length !== 4) {
    return [0, 0, 0, 0];
  }
  return [
    typeof value[0] === "number" ? value[0] : 0,
    typeof value[1] === "number" ? value[1] : 0,
    typeof value[2] === "number" ? value[2] : 0,
    typeof value[3] === "number" ? value[3] : 0,
  ];
}

function parseStoredLevelStats(data: DocumentData): StoredLevelStats {
  return {
    completedCount:
      typeof data.completedCount === "number" ? data.completedCount : 0,
    inProgressCount:
      typeof data.inProgressCount === "number" ? data.inProgressCount : 0,
    starCounts: asStarCounts(data.starCounts),
    avgScore: typeof data.avgScore === "number" ? data.avgScore : null,
    scoreSampleCount:
      typeof data.scoreSampleCount === "number" ? data.scoreSampleCount : 0,
    avgDurationSeconds:
      typeof data.avgDurationSeconds === "number"
        ? data.avgDurationSeconds
        : null,
    durationSampleCount:
      typeof data.durationSampleCount === "number"
        ? data.durationSampleCount
        : 0,
    participantCount:
      typeof data.participantCount === "number" ? data.participantCount : 0,
  };
}

export function useEventContentOverview(eventId: EventCollectionId | null) {
  const dataSource = useDataSource();
  const { locale } = useAdminTranslation();
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [statsByLevelId, setStatsByLevelId] = useState<
    Map<string, StoredLevelStats>
  >(new Map());
  const [compute, setCompute] = useState<EventTask | null>(null);
  const [unpublished, setUnpublished] = useState(false);
  const [loading, setLoading] = useState(false);
  const loadGeneration = useRef(0);

  useEffect(() => {
    loadGeneration.current += 1;
    const generation = loadGeneration.current;

    if (!eventId) {
      setCatalog(null);
      setStatsByLevelId(new Map());
      setCompute(null);
      setUnpublished(false);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setCatalog(null);
    setStatsByLevelId(new Map());
    setCompute(null);
    setUnpublished(false);

    const load = async () => {
      try {
        const eventEntity = await dataSource.fetchEntity<Event>({
          path: buildEventsPath({ accountId: eventId.accountId }),
          collection: eventsCollection,
          entityId: eventId.eventId,
        });
        const catalogId = eventContentScopeId(
          eventId,
          eventEntity?.values?.contentReleaseRef
        );

        if (!catalogId) {
          if (cancelled) {
            return;
          }
          setUnpublished(true);
          setCatalog({ units: [], levels: [], stories: [] });
          return;
        }

        const [units, levels, stories] = await Promise.all([
          dataSource.fetchCollection({
            path: buildUnitsPath(catalogId),
            collection: buildUnitCollection(catalogId),
          }),
          dataSource.fetchCollection({
            path: buildLevelsPath(catalogId),
            collection: buildLevelsCollection(catalogId),
          }),
          dataSource.fetchCollection({
            path: buildStoriesPath(catalogId),
            collection: buildStoriesCollection(catalogId),
          }),
        ]);

        if (cancelled || generation !== loadGeneration.current) {
          return;
        }

        setCatalog({
          units: units.map((entity) => ({
            id: entity.id,
            code: String(entity.values?.code ?? ""),
            name: (entity.values?.name as Record<string, string>) ?? {},
            unlockMode: String(entity.values?.unlockMode ?? ""),
            accessCode: String(entity.values?.accessCode ?? ""),
          })),
          levels: levels.map((entity) => {
            const unitRef = entity.values?.unitRef as { id?: string } | undefined;
            const storyRef = entity.values?.storyRef as { id?: string } | undefined;
            return {
              id: entity.id,
              code: String(entity.values?.code ?? ""),
              order:
                typeof entity.values?.order === "number" ? entity.values.order : 0,
              modality: String(entity.values?.modality ?? ""),
              unitId: unitRef?.id ?? "",
              difficulty:
                typeof entity.values?.difficulty === "string"
                  ? entity.values.difficulty
                  : undefined,
              playFormat:
                typeof entity.values?.playFormat === "string"
                  ? entity.values.playFormat
                  : undefined,
              storyRef: storyRef?.id ? { id: storyRef.id } : undefined,
            };
          }),
          stories: stories.map((entity) => ({
            id: entity.id,
            code: String(entity.values?.code ?? ""),
            title: (entity.values?.title as Record<string, string>) ?? {},
          })),
        });
      } finally {
        if (!cancelled && generation === loadGeneration.current) {
          setLoading(false);
        }
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [dataSource, eventId]);

  useEffect(() => {
    if (!eventId) {
      setCompute(null);
      setStatsByLevelId(new Map());
      return;
    }

    const db = getFirestore(getApp());
    const unsubEvent = onSnapshot(
      doc(db, eventTaskPath(eventId.accountId, eventId.eventId, "levelStats")),
      (snapshot) => {
        setCompute(parseEventTask(snapshot.data()));
      }
    );
    const unsubStats = onSnapshot(
      collection(
        db,
        "accounts",
        eventId.accountId,
        "events",
        eventId.eventId,
        "levelsStats"
      ),
      (snapshot) => {
        const next = new Map<string, StoredLevelStats>();
        for (const statsDoc of snapshot.docs) {
          next.set(statsDoc.id, parseStoredLevelStats(statsDoc.data()));
        }
        setStatsByLevelId(next);
      }
    );

    return () => {
      unsubEvent();
      unsubStats();
    };
  }, [eventId]);

  const overview: EventContentOverview = useMemo(() => {
    if (!catalog) {
      return { participantCount: 0, statsLoaded: false, units: [] };
    }
    return aggregateEventContentOverview({
      ...catalog,
      statsByLevelId,
      locale,
    });
  }, [catalog, locale, statsByLevelId]);

  return {
    overview,
    loading,
    catalogLoaded: catalog != null,
    unpublished,
    compute,
  };
}
